import { useTranslation } from '@rbx/intl';
import React, { FC } from 'react';
import { ThemeMode, Typography, Grid, UIThemeProvider } from '@rbx/ui';
import StandardMarkdown from '@modules/ui/markdown/components/StandardMarkdown';
import EngineMarkdown from '@modules/ui/markdown/components/EngineMarkdown';
import useDeviceInfo from '@modules/miscellaneous/common/hooks/useDeviceInfo';
import { useThemeMode } from '@rbx/settings';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import useReferenceSummaryMemberTableStyles from './ReferenceSummaryMemberTable.styles';
import useReferenceSummaryMemberTableStylesV2 from './ReferenceSummaryMemberTableV2.styles';
import { TypeAliasDatumItem } from '../../types';

const getTooltipDescription = (
  translate: ReturnType<typeof useTranslation>['translate'],
  tooltipProps: TypeAliasDatumItem['tooltipProps'],
) => {
  // Tooltip description can be string or a translation key (with optional
  // translation parameters)
  const { description, descriptionTranslKey, descriptionTranslKeyMap } = tooltipProps ?? {};
  let tooltipDescription: string | undefined;
  if (descriptionTranslKey) {
    tooltipDescription = descriptionTranslKeyMap
      ? translate(descriptionTranslKey, descriptionTranslKeyMap)
      : translate(descriptionTranslKey) || descriptionTranslKey;
  } else {
    tooltipDescription = description;
  }
  return tooltipDescription;
};

// TODO: move once reusability needed
const getInverseTheme = (themeMode: ThemeMode): ThemeMode =>
  themeMode === 'light' ? 'dark' : 'light';

export const clickableMemberTypeTooltipContentDataTooltipId = 'tooltip-type-link';

const ReferenceMemberTypeTooltipTitle: FC<
  React.PropsWithChildren<{
    url?: string;
    tooltipProps: TypeAliasDatumItem['tooltipProps'];
    engineRevampEnabled?: boolean;
  }>
> = ({ url, tooltipProps, engineRevampEnabled = false }) => {
  const { themeMode } = useThemeMode();
  if (!tooltipProps) throw new Error(`Retrieving tooltip without tooltipProps`);
  const { isMobileDevice } = useDeviceInfo();
  const { translate } = useTranslation();
  const defaultStyles = useReferenceSummaryMemberTableStyles();
  const v2Styles = useReferenceSummaryMemberTableStylesV2();
  const {
    classes: { memberTypeTooltip, memberTypeTooltipLink },
  } = engineRevampEnabled ? v2Styles : defaultStyles;
  const { title, subtitle } = tooltipProps;

  return (
    <div className={memberTypeTooltip}>
      <Grid>
        <Grid item XSmall={12} marginBottom={1}>
          <Typography variant='largeLabel2' component='label'>
            {isMobileDevice && url ? (
              <DynamicallyRouted.Link
                className={memberTypeTooltipLink}
                data-tooltip-id={clickableMemberTypeTooltipContentDataTooltipId}
                href={url}
                color='inherit'>
                {title}
              </DynamicallyRouted.Link>
            ) : (
              <span>{title}</span>
            )}
          </Typography>
        </Grid>
        {subtitle && (
          <Grid item XSmall={12}>
            <Typography
              variant='footer'
              color='inherit'
              component='label'
              className='tooltipSubtitle'>
              {subtitle}
            </Typography>
          </Grid>
        )}
        <Grid item XSmall={12}>
          {engineRevampEnabled ? (
            <EngineMarkdown
              overrideParagraphStyle={{
                marginTop: '8px',
                marginBottom: 0,
              }}>
              {getTooltipDescription(translate, tooltipProps)}
            </EngineMarkdown>
          ) : (
            <UIThemeProvider theme={getInverseTheme(themeMode)} cssBaselineMode='disabled'>
              <StandardMarkdown
                paragraphProps={{
                  variant: 'body1',
                  className: 'tooltipBody',
                }}
                inlineCodeProps={{
                  whiteSpace: 'auto-phrase',
                }}>
                {getTooltipDescription(translate, tooltipProps)}
              </StandardMarkdown>
            </UIThemeProvider>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default ReferenceMemberTypeTooltipTitle;
